import type { ReactNode } from 'react'
import React, { useEffect, useRef } from 'react'
import { useFormContext, useFormState, useWatch } from 'react-hook-form'
import { View } from 'react-native'

import type { GroupableElement } from '@flows-ui/utils/questionGroups'
import {
  getQuestionGroups,
  getQuestionKeys,
  isQuestionAnswered,
  needsConfirmation,
} from '@flows-ui/utils/questionGroups'
import type { AnyStepContentElement } from '@flows/core/types/stepContentTypes'

export const FLOW_HEADER_ID = 'flow-header'
const SCROLL_GAP = 24

const SCROLL_DELAY = 180
const SCROLL_DURATION = 600

const easeOutQuint = (progress: number) => 1 - (1 - progress) ** 5

const smoothScrollTo = (top: number) => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    window.scrollTo(0, top)
    return () => undefined
  }
  const startY = window.scrollY
  let start: number | undefined
  let frame = 0
  const stop = () => {
    cancelAnimationFrame(frame)
    window.removeEventListener('wheel', stop)
    window.removeEventListener('touchstart', stop)
  }
  const step = (now: number) => {
    start ??= now
    const progress = Math.min((now - start) / SCROLL_DURATION, 1)
    window.scrollTo(0, startY + (top - startY) * easeOutQuint(progress))
    if (progress < 1) {
      frame = requestAnimationFrame(step)
    } else {
      stop()
    }
  }
  window.addEventListener('wheel', stop, { passive: true })
  window.addEventListener('touchstart', stop, { passive: true })
  frame = requestAnimationFrame(step)
  return stop
}

export const ProgressiveQuestions = ({
  elements,
  renderItem,
  getItemKey,
  getSpaceBefore,
}: {
  elements: readonly AnyStepContentElement[]
  renderItem: (item: AnyStepContentElement, index: number) => ReactNode
  getItemKey: (item: AnyStepContentElement, index: number) => string
  getSpaceBefore: (item: AnyStepContentElement) => number
}) => {
  const { control } = useFormContext()
  const groupable = elements as readonly GroupableElement[]
  const questions = groupable.filter((element) => getQuestionKeys(element).length > 0)
  const names = questions.flatMap((question) => [...getQuestionKeys(question)])
  const watched = useWatch({ control, name: names }) as unknown[]
  const values = Object.fromEntries(names.map((name, index) => [name, watched[index]]))
  const { errors, touchedFields, dirtyFields } = useFormState({ control, name: names })
  const groups = getQuestionGroups(groupable)

  const current = questions.findIndex(
    (question) => !isQuestionAnswered(question, { values, errors, touchedFields, dirtyFields })
  )

  const groupStarts = useRef<Record<number, HTMLElement | null>>({})
  const previous = useRef(current)
  useEffect(() => {
    const next = groupStarts.current[current]
    const advanced = previous.current >= 0 && current > previous.current
    previous.current = current
    if (!advanced || !next) {
      return undefined
    }
    let stopScroll: () => void = () => undefined
    const timer = setTimeout(() => {
      const headerBottom =
        document.getElementById(FLOW_HEADER_ID)?.getBoundingClientRect().bottom ?? 0
      stopScroll = smoothScrollTo(
        window.scrollY + next.getBoundingClientRect().top - headerBottom - SCROLL_GAP
      )
    }, SCROLL_DELAY)
    return () => {
      clearTimeout(timer)
      stopScroll()
    }
  }, [current])

  return (
    <>
      {elements.map((item, index) => {
        const group = groups[index]
        const isGroupStart = group >= 0 && group !== groups[index - 1]
        const isDimmed = current >= 0 && group > current && !needsConfirmation(questions[group])
        return (
          <React.Fragment key={getItemKey(item, index)}>
            {index > 0 && <View style={{ height: getSpaceBefore(item) }} />}
            <View
              ref={
                isGroupStart
                  ? (node) => {
                      groupStarts.current[group] = node as unknown as HTMLElement | null
                    }
                  : undefined
              }
              className={`transition-opacity duration-500 ${isDimmed ? 'opacity-30' : 'opacity-100'}`}
            >
              {renderItem(item, index)}
            </View>
          </React.Fragment>
        )
      })}
    </>
  )
}
